import {
  BadgeOutlined,
  ConfirmationNumberOutlined,
  DeleteOutline,
  EditOutlined,
  NotesOutlined,
  Payment,
  ShoppingBagOutlined,
} from "@mui/icons-material";
import { Box, Button, Chip, Typography } from "@mui/material";
import PropTypes from "prop-types";
import {
  formatCurrency,
  getCustomerDisplayName,
} from "../utils";

const voucherTypeLabel = {
  MONTO: "Monto",
  PRODUCTO: "Producto",
  CATEGORIA: "Categoría",
};

function EditButton({ label, ariaLabel, onClick }) {
  return (
    <Button
      size="small"
      startIcon={<EditOutlined />}
      aria-label={ariaLabel}
      onClick={onClick}
      sx={{
        px: 0.75,
        color: "#2C3087",
        textTransform: "none",
        fontSize: 11,
        fontWeight: 800,
        "&:hover": { bgcolor: "#F3F2FF" },
      }}
    >
      {label}
    </Button>
  );
}

EditButton.propTypes = {
  label: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

export default function SaleSummary({
  cartItems,
  customer,
  paymentMethod,
  voucher,
  observaciones = "",
  totals,
  onEditStep,
  onClear,
}) {
  const totalUnits = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const paymentRequired = totals.amountDue > 0.01;
  const paymentLabel = paymentRequired ? paymentMethod?.label || "Pendiente" : "No requerido · cubierto";

  return (
    <Box
      data-testid="sale-summary"
      sx={{
        bgcolor: "#fff",
        border: "1px solid #E2E8F0",
        borderRadius: "20px",
        boxShadow: "0 18px 44px rgba(15, 23, 42, 0.09)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 1.75, bgcolor: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Box>
            <Typography sx={{ fontSize: 10.5, fontWeight: 900, color: "#2C3087", letterSpacing: ".08em" }}>
              RESUMEN DE LA VENTA
            </Typography>
            <Typography component="h2" sx={{ fontSize: 19, lineHeight: 1.2, fontWeight: 900, color: "#172033", mt: 0.25 }}>
              Selecciones actuales
            </Typography>
          </Box>
          {totalUnits > 0 && (
            <Chip label={`${totalUnits} u.`} size="small" sx={{ bgcolor: "#E8F7F2", color: "#18735F", fontWeight: 900 }} />
          )}
        </Box>
      </Box>

      <Box sx={{ p: 1.5, pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.75 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <ShoppingBagOutlined sx={{ color: "#2C3087", fontSize: 19 }} />
            <Typography sx={{ color: "#263247", fontSize: 12.5, fontWeight: 900 }}>Productos</Typography>
          </Box>
          <EditButton
            label={totalUnits ? "Editar" : "Agregar"}
            ariaLabel={totalUnits ? "Editar productos" : "Agregar productos"}
            onClick={() => onEditStep(0)}
          />
        </Box>

        {cartItems.length === 0 ? (
          <Box sx={{ py: 1.75, px: 1.25, borderRadius: "12px", bgcolor: "#F8FAFC", textAlign: "center" }}>
            <Typography sx={{ color: "#94A3B8", fontSize: 12 }}>Todavía no seleccionaste productos.</Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 174, overflowY: "auto", pr: 0.5 }}>
            {cartItems.map((item) => {
              const coveredQuantity = Number(totals.voucherCoverageByProduct?.[item.id] || 0);
              return (
                <Box
                  key={item.id}
                  sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 1, py: 0.75, borderBottom: "1px solid #EEF2F7" }}
                >
                  <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography noWrap sx={{ minWidth: 0, color: "#475569", fontSize: 12.5 }}>
                      <Box component="span" sx={{ color: "#172033", fontWeight: 900 }}>{item.quantity}×</Box> {item.name}
                    </Typography>
                    {coveredQuantity > 0 && (
                      <Chip label={`${coveredQuantity} cub.`} size="small" sx={{ height: 17, bgcolor: "#E0F5EF", color: "#18735F", fontSize: 8.5, fontWeight: 900 }} />
                    )}
                  </Box>
                  <Typography sx={{ color: "#172033", fontSize: 12.5, fontWeight: 800 }}>
                    {formatCurrency(item.price * item.quantity)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {cartItems.length > 0 && (
          <Button
            size="small"
            startIcon={<DeleteOutline />}
            onClick={onClear}
            sx={{ mt: 0.75, px: 0.75, color: "#C62828", textTransform: "none", fontSize: 11, fontWeight: 800, "&:hover": { bgcolor: "#FFF1F1" } }}
          >
            Cancelar venta
          </Button>
        )}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "auto minmax(0, 1fr) auto", gap: 1, alignItems: "center", px: 1.5, py: 1.25, borderTop: "1px solid #EEF2F7" }}>
        <Box sx={{ width: 34, height: 34, borderRadius: "10px", display: "grid", placeItems: "center", bgcolor: voucher ? "#FFF3DB" : "#F1F5F9" }}>
          <ConfirmationNumberOutlined sx={{ color: voucher ? "#9A6209" : "#94A3B8", fontSize: 19 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: "#94A3B8", fontSize: 10, fontWeight: 900 }}>BENEFICIO</Typography>
          <Typography noWrap sx={{ color: voucher ? "#172033" : "#94A3B8", fontSize: 12.5, fontWeight: 800 }}>
            {voucher?.name || "Sin voucher"}
          </Typography>
          {voucher && (
            <Typography noWrap sx={{ color: "#9A6209", fontSize: 10.5 }}>
              {voucherTypeLabel[voucher.type]}
              {voucher.type === "MONTO"
                ? ` · hasta ${formatCurrency(voucher.amount)}`
                : ` · ${totals.voucherCoveredUnits}/${voucher.units} u. aplicadas`}
            </Typography>
          )}
        </Box>
        <EditButton label="Editar" ariaLabel="Editar voucher físico" onClick={() => onEditStep(0)} />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "auto minmax(0, 1fr) auto", gap: 1, alignItems: "center", px: 1.5, py: 1.25, borderTop: "1px solid #EEF2F7" }}>
        <Box sx={{ width: 34, height: 34, borderRadius: "10px", display: "grid", placeItems: "center", bgcolor: customer ? "#E8F7F2" : "#F1F5F9" }}>
          <BadgeOutlined sx={{ color: customer ? "#18735F" : "#94A3B8", fontSize: 19 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: "#94A3B8", fontSize: 10, fontWeight: 900 }}>CLIENTE</Typography>
          <Typography noWrap sx={{ color: customer ? "#172033" : "#94A3B8", fontSize: 12.5, fontWeight: 800 }}>
            {customer ? getCustomerDisplayName(customer) : "Pendiente de identificar"}
          </Typography>
          {customer && (
            <Typography noWrap sx={{ color: "#64748B", fontSize: 10.5 }}>
              {customer.isCommunityMember ? `${customer.communityRole} UPrO · Sin IVA` : "Consumidor final · IVA discriminado"}
            </Typography>
          )}
        </Box>
        {totalUnits > 0 && (
          <EditButton
            label={customer ? "Editar" : "Cargar"}
            ariaLabel={customer ? "Editar cliente" : "Cargar cliente"}
            onClick={() => onEditStep(1)}
          />
        )}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "auto minmax(0, 1fr) auto", gap: 1, alignItems: "center", px: 1.5, py: 1.25, borderTop: "1px solid #EEF2F7" }}>
        <Box sx={{ width: 34, height: 34, borderRadius: "10px", display: "grid", placeItems: "center", bgcolor: customer ? "#F3F2FF" : "#F1F5F9" }}>
          <Payment sx={{ color: customer ? "#2C3087" : "#94A3B8", fontSize: 19 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: "#94A3B8", fontSize: 10, fontWeight: 900 }}>MEDIO DE PAGO</Typography>
          <Typography noWrap sx={{ color: customer ? "#172033" : "#94A3B8", fontSize: 12.5, fontWeight: 800 }}>
            {customer ? paymentLabel : "Disponible después del cliente"}
          </Typography>
        </Box>
        {customer && paymentRequired && <EditButton label="Editar" ariaLabel="Editar medio de pago" onClick={() => onEditStep(2)} />}
      </Box>

      {Boolean(observaciones.trim()) && (
        <Box
          data-testid="summary-observaciones"
          sx={{ display: "grid", gridTemplateColumns: "auto minmax(0, 1fr) auto", gap: 1, alignItems: "center", px: 1.5, py: 1.25, borderTop: "1px solid #EEF2F7" }}
        >
          <Box sx={{ width: 34, height: 34, borderRadius: "10px", display: "grid", placeItems: "center", bgcolor: "#FFF3DB" }}>
            <NotesOutlined sx={{ color: "#9A6209", fontSize: 19 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: "#94A3B8", fontSize: 10, fontWeight: 900 }}>OBSERVACIONES</Typography>
            <Typography sx={{ color: "#172033", fontSize: 12, fontWeight: 700, whiteSpace: "pre-line", overflowWrap: "anywhere" }}>
              {observaciones.trim()}
            </Typography>
          </Box>
          <EditButton label="Editar" ariaLabel="Editar observaciones" onClick={() => onEditStep(1)} />
        </Box>
      )}

      <Box sx={{ px: 1.75, py: 1.5, bgcolor: "#FBFCFE", borderTop: "1px solid #E2E8F0" }}>
        {(totals.voucherApplied > 0 || totals.descuentoEventoMasivo > 0) && (
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography sx={{ color: "#64748B", fontSize: 11 }}>Subtotal</Typography>
            <Typography sx={{ color: "#475569", fontSize: 11 }}>{formatCurrency(totals.total)}</Typography>
          </Box>
        )}
        {customer?.isCommunityMember && (
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography sx={{ color: "#2C7A67", fontSize: 11, fontWeight: 700 }}>Comunidad UPrO</Typography>
          </Box>
        )}
        {totals.descuentoEventoMasivo > 0 && <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}><Typography sx={{ color: "#2C3087", fontSize: 11 }}>Descuento del evento ({totals.porcentajeDescuentoEvento} %)</Typography><Typography sx={{ color: "#2C3087", fontSize: 11 }}>− {formatCurrency(totals.descuentoEventoMasivo)}</Typography></Box>}
        {totals.voucherApplied > 0 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography sx={{ color: "#9A6209", fontSize: 11, fontWeight: 700 }}>Voucher aplicado</Typography>
            <Typography sx={{ color: "#9A6209", fontSize: 11, fontWeight: 800 }}>− {formatCurrency(totals.voucherApplied)}</Typography>
          </Box>
        )}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}><Typography sx={{ fontSize: 11 }}>Factura B · {customer?.isCommunityMember ? "Exento" : "IVA incluido 21 %"}</Typography><Typography sx={{ fontSize: 11 }}>IVA {formatCurrency(totals.importeIva || 0)}</Typography></Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}><Typography sx={{ fontSize: 11 }}>{customer?.isCommunityMember ? "Monto exento" : "Neto gravado"}</Typography><Typography sx={{ fontSize: 11 }}>{formatCurrency(totals.importeNeto ?? totals.amountDue)}</Typography></Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <Typography sx={{ color: "#172033", fontSize: 13, fontWeight: 900 }}>
            {totals.voucherApplied ? "Saldo a cobrar" : "Total"}
          </Typography>
          <Typography data-testid="sale-total" sx={{ color: "#2C3087", fontSize: 25, lineHeight: 1, fontWeight: 900 }}>
            {formatCurrency(totals.amountDue)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

SaleSummary.propTypes = {
  cartItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  customer: PropTypes.object,
  paymentMethod: PropTypes.object,
  voucher: PropTypes.object,
  observaciones: PropTypes.string,
  totals: PropTypes.object.isRequired,
  onEditStep: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};
